/**
 * Admin App Tab – application-wide configuration (app_config).
 * Visible only in Admin Panel (admin role).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { adminApi, AppConfigItem } from '@/api/admin';

function parseAddValues(value: string): Record<string, unknown> | null {
  const t = value.trim();
  if (!t) return null;
  try {
    const o = JSON.parse(t);
    return typeof o === 'object' && o !== null ? (o as Record<string, unknown>) : null;
  } catch {
    return undefined as unknown as null;
  }
}

export const AdminAppTab: React.FC = () => {
  const [configs, setConfigs] = useState<AppConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AppConfigItem | null>(null);
  const [formFunction, setFormFunction] = useState('');
  const [formBoolValue, setFormBoolValue] = useState<boolean>(false);
  const [formAddValues, setFormAddValues] = useState('');
  const [formAddValuesError, setFormAddValuesError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getAppConfig();
      setConfigs(data ?? []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load app config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const openCreate = () => {
    setEditingItem(null);
    setFormFunction('');
    setFormBoolValue(false);
    setFormAddValues('');
    setFormAddValuesError(null);
    setSubmitError(null);
    setDialogOpen(true);
  };

  const openEdit = (item: AppConfigItem) => {
    setEditingItem(item);
    setFormFunction(item.function);
    setFormBoolValue(item.bool_value ?? false);
    setFormAddValues(item.add_values != null ? JSON.stringify(item.add_values, null, 2) : '');
    setFormAddValuesError(null);
    setSubmitError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setFormAddValuesError(null);
    setSubmitError(null);
    const addValues = parseAddValues(formAddValues);
    if (formAddValues.trim() && addValues === undefined) {
      setFormAddValuesError('Invalid JSON');
      return;
    }
    try {
      if (editingItem) {
        await adminApi.updateAppConfig(editingItem.id, {
          function: formFunction || undefined,
          bool_value: formBoolValue,
          add_values: addValues ?? undefined,
        });
      } else {
        if (!formFunction.trim()) {
          setSubmitError('Function is required');
          return;
        }
        await adminApi.createAppConfig({
          function: formFunction.trim(),
          bool_value: formBoolValue,
          add_values: addValues ?? undefined,
        });
      }
      setDialogOpen(false);
      await loadConfigs();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Request failed');
    }
  };

  const handleDelete = async (item: AppConfigItem) => {
    if (!window.confirm(`Delete config "${item.function}"?`)) return;
    try {
      await adminApi.deleteAppConfig(item.id);
      await loadConfigs();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Application configuration</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Add
        </Button>
      </Box>
      {configs.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">No configuration entries.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {configs.map((item) => (
            <Paper key={item.id} sx={{ p: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {item.function}
                </Typography>
                <Typography variant="body2" color="text.secondary" component="span">
                  {item.bool_value != null && `bool_value: ${String(item.bool_value)}`}
                  {item.bool_value != null && item.add_values != null && Object.keys(item.add_values).length > 0 && ' · '}
                  {item.add_values != null && Object.keys(item.add_values).length > 0 &&
                    `add_values: ${JSON.stringify(item.add_values)}`}
                  {item.bool_value == null && (!item.add_values || Object.keys(item.add_values).length === 0) && '—'}
                </Typography>
              </Box>
              <Box>
                <IconButton size="small" onClick={() => openEdit(item)} aria-label="Edit">
                  <Edit />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => handleDelete(item)} aria-label="Delete">
                  <Delete />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit config' : 'Create config'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <TextField
            label="Function (key)"
            value={formFunction}
            onChange={(e) => setFormFunction(e.target.value)}
            disabled={!!editingItem}
            fullWidth
            required
          />
          <FormControl fullWidth size="small" required>
            <InputLabel>bool_value</InputLabel>
            <Select
              value={formBoolValue ? 'true' : 'false'}
              label="bool_value"
              onChange={(e) => setFormBoolValue(e.target.value === 'true')}
            >
              <MenuItem value="true">true</MenuItem>
              <MenuItem value="false">false</MenuItem>
            </Select>
            <FormHelperText>Required</FormHelperText>
          </FormControl>
          <TextField
            label="add_values (JSON)"
            value={formAddValues}
            onChange={(e) => {
              setFormAddValues(e.target.value);
              setFormAddValuesError(null);
            }}
            fullWidth
            multiline
            minRows={8}
            error={!!formAddValuesError}
            helperText={formAddValuesError}
            placeholder='{"key": "value"}'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingItem ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
